import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@remix-run/react";
import type { ReactNode } from "react";
import Cookies from "js-cookie";

interface Props {
  title: ReactNode;
  text?: ReactNode | string;
  redirect?: string;
  children?: ReactNode;
  dismissible?: boolean;
  autoHideAfter?: number; // time in seconds before banner disappears
  bannerId?: string; // unique identifier for this banner for cookie storage
  showDoNotShowAgain?: boolean; // whether to show "do not show again" option
}

// Cookie name prefix for banners
const BANNER_COOKIE_PREFIX = "banner_dismissed_";
// Default cookie expiration in days
const COOKIE_EXPIRATION_DAYS = 30;

// Helper function to check if banner is dismissed via cookie
const isBannerDismissed = (bannerId?: string): boolean => {
  if (!bannerId) return false;
  const cookieName = `${BANNER_COOKIE_PREFIX}${bannerId}`;
  return Cookies.get(cookieName) === "true";
};

// Custom hook to detect if the component is hydrated
function useHydrated() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return isHydrated;
}

export default function InfoBanner({ 
  title = "Note", 
  text = "", 
  redirect, 
  children, 
  dismissible = false,
  autoHideAfter,
  bannerId,
  showDoNotShowAgain = false
}: Props) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(true);
  const isHydrated = useHydrated();
  
  // Only check cookie status after hydration
  const isDismissed = isHydrated && bannerId ? isBannerDismissed(bannerId) : false;

  // Set up auto-hide timer
  useEffect(() => {
    if (autoHideAfter && autoHideAfter > 0 && visible) {
      const timer = setTimeout(() => {
        setVisible(false);
      }, autoHideAfter * 1000);
      
      return () => clearTimeout(timer);
    }
  }, [autoHideAfter, visible]);

  // Function to dismiss the banner and optionally store preference in cookie
  const dismissBanner = (doNotShowAgain = false) => {
    setVisible(false);
    
    if (doNotShowAgain && bannerId) {
      const cookieName = `${BANNER_COOKIE_PREFIX}${bannerId}`;
      Cookies.set(cookieName, "true", { expires: COOKIE_EXPIRATION_DAYS });
    }
  };

  // Don't render at all until hydrated and if cookie says it's dismissed
  if (!isHydrated || isDismissed || !visible) return null;

  return (
    <div className="not-prose rounded-md border border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900">
      <div className="rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400 dark:text-blue-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          <div className="ml-3 w-full">
            <div className="flex justify-between">
              <h3 className="text-sm font-medium leading-5 text-blue-900 dark:text-blue-50">{title}</h3>
              {dismissible && (
                <button 
                  onClick={() => dismissBanner(false)}
                  className="text-blue-400 hover:text-blue-600 dark:text-blue-300 dark:hover:text-blue-100"
                  aria-label="Close"
                >
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path 
                      fillRule="evenodd" 
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" 
                      clipRule="evenodd" 
                    />
                  </svg>
                </button>
              )}
            </div>
            <div className="mt-2 text-sm leading-5 text-blue-800 dark:text-blue-100">
              <div>
                {text}{" "}
                {redirect && (
                  <Link className="mt-2 text-blue-900 underline dark:text-blue-50" to={redirect}>
                    {t("shared.goTo")} {redirect}
                  </Link>
                )}
                {children}
              </div>
            </div>
            {showDoNotShowAgain && bannerId && (
              <div className="mt-3 flex justify-end">
                <button
                  onClick={() => dismissBanner(true)}
                  className="text-xs text-blue-700 hover:text-blue-900 dark:text-blue-200 dark:hover:text-blue-50"
                >
                  Don't show again
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
